import { PrimaryButton, SecondaryButton } from '@/components/buttons';
import { Loading } from '@/components/loading';
import { Body3 } from '@/components/typography';
import { cn } from '@/lib/utils';
import { ProjectReportingType, updateProjectSettings, useProject } from '@/modules/Customers/Customers.api';
import { routes } from '@/modules/Router';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// Extended project type for editing - this should eventually be moved to a proper API file
type EditableProject = {
  project_id: string;
  project_name: string;
  project_code: string;
  client_name: string;
  reporting_type: ProjectReportingType;
  farm_csv_enabled: boolean;
  project_csv_enabled: boolean;
  farm_report_subtitle?: string;
  farm_report_introduction?: string;
  project_report_subtitle?: string;
  project_report_introduction?: string;
  report_disclaimer?: string;
};

export function EditProjectForm() {
  const { projectId } = useParams<{ projectId: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<EditableProject>>({});

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: project, status } = useProject(projectId || '');

  // Initialize form data when project data is available
  React.useEffect(() => {
    if (project) {
      const reportingType = (project.reporting_type as ProjectReportingType) || 'farm_only';

      setFormData({
        project_id: project.project_id,
        project_name: project.project_name,
        project_code: project.project_id, // Using project_id as code for now
        client_name: project.breadcrumb.client.name,
        reporting_type: reportingType,
        farm_csv_enabled: project.farm_csv_enabled,
        // Auto-tick project CSV if project_only is selected
        project_csv_enabled: reportingType === 'project_only' ? true : project.project_csv_enabled || false,
        farm_report_subtitle: project.farm_report_subtitle || '',
        farm_report_introduction: project.farm_report_introduction || '',
        project_report_subtitle: project.project_report_subtitle || '',
        project_report_introduction: project.project_report_introduction || '',
        report_disclaimer: project.report_disclaimer || '',
      });
    }
  }, [project]);

  if (status === 'error') {
    toast.error('Failed to fetch project details');
    return;
  }

  if (status === 'pending') {
    return <Loading />;
  }

  if (project === undefined || project === null) {
    toast.error('Failed to fetch project details');
    return;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!projectId) {
      toast.error('Project ID is required');
      return;
    }

    try {
      setIsLoading(true);

      await updateProjectSettings(projectId, formData);

      // Invalidate React Query cache to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['Project', projectId] });
      await queryClient.invalidateQueries({ queryKey: ['ProjectList'] });

      toast.success('Project settings updated successfully!');
      navigate(routes.projects);
    } catch (error) {
      console.error('Error updating project settings:', error);
      toast.error('Failed to update project settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof EditableProject, value: string | boolean) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      // When reporting type changes to "project_only", auto-tick project CSV and untick farm CSV
      if (field === 'reporting_type') {
        if (value === 'project_only') {
          updated.project_csv_enabled = true;
          updated.farm_csv_enabled = false;
        } else if (value === 'farm_only') {
          updated.project_csv_enabled = false;
        }
      }

      return updated;
    });
  };

  return (
    <div className={cn('flex flex-grow justify-center bg-secondary-15 items-center p-16 place-items-center mb-8')}>
      <form onSubmit={handleSubmit} className={cn('flex flex-col gap-6 min-w-[450px]')}>
        <div>
          <h2 className={cn('text-lg font-semibold text-gray-700')}>Edit Project - {formData.client_name}</h2>
          <Body3 className="text-neutral-500">Edit the details of the project below</Body3>
        </div>

        <div className={cn('flex flex-col gap-4')}>
          <h3 className={cn('text-md font-semibold text-gray-700')}>Project</h3>

          {/* Read only: Project Name and Project code  */}
          <div className={cn('flex flex-row items-center')}>
            <span className={cn('text-sm font-medium text-gray-600')}>Project Code:</span>
            &nbsp;
            <span className={cn('text-sm font-bold text-gray-600')}>{formData.project_code}</span>
          </div>
          <div className={cn('flex flex-row items-center')}>
            <span className={cn('text-sm font-medium text-gray-600')}>Project Name:</span>
            &nbsp;
            <input
              type="text"
              id="project_name"
              value={formData.project_name || ''}
              onChange={(e) => handleInputChange('project_name', e.target.value)}
              className={cn('px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')}
            />
          </div>

          <div className={cn('flex flex-col gap-2')}>
            <label htmlFor="reporting_type" className={cn('text-sm font-medium text-gray-600')}>
              Reporting Type
            </label>
            <select
              id="reporting_type"
              value={formData.reporting_type || 'farm_only'}
              onChange={(e) => handleInputChange('reporting_type', e.target.value as ProjectReportingType)}
              className={cn('px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')}
            >
              <option value="farm_only">Farm only</option>
              <option value="project_only">Project only</option>
              <option value="project_and_farm">Project and Farm</option>
            </select>
          </div>

          <div className={cn('flex flex-col gap-2')}>
            <span className={cn('text-sm font-medium text-gray-600')}>CSV Availability</span>

            <div className={cn('flex items-center gap-2')}>
              <input
                type="checkbox"
                id="farm_csv"
                checked={formData.farm_csv_enabled || false}
                onChange={(e) => handleInputChange('farm_csv_enabled', e.target.checked)}
                disabled={formData.reporting_type === 'project_only'}
                className={cn('w-4 h-4', formData.reporting_type === 'project_only' && 'opacity-50')}
              />
              <label htmlFor="farm_csv" className={cn('text-sm text-gray-600', formData.reporting_type === 'project_only' && 'opacity-50')}>
                Farm level CSV download
              </label>
            </div>

            <div className={cn('flex items-center gap-2')}>
              <input
                type="checkbox"
                id="project_csv"
                checked={formData.project_csv_enabled || false}
                onChange={(e) => handleInputChange('project_csv_enabled', e.target.checked)}
                disabled={formData.reporting_type === 'farm_only'}
                className={cn('w-4 h-4', formData.reporting_type === 'farm_only' && 'opacity-50')}
              />
              <label htmlFor="project_csv" className={cn('text-sm text-gray-600', formData.reporting_type === 'farm_only' && 'opacity-50')}>
                Project level CSV download
              </label>
            </div>
          </div>
        </div>

        <div className={cn('flex flex-col gap-4')}>
          <h3 className={cn('text-md font-semibold text-gray-700')}>Farm Report</h3>

          <div className={cn('flex flex-col gap-2')}>
            <label
              htmlFor="farm_subtitle"
              className={cn('text-sm font-medium text-gray-600', formData.reporting_type === 'project_only' && 'opacity-50')}
            >
              Subtitle
            </label>
            <input
              type="text"
              id="farm_subtitle"
              value={formData.farm_report_subtitle || ''}
              onChange={(e) => handleInputChange('farm_report_subtitle', e.target.value)}
              disabled={formData.reporting_type === 'project_only'}
              className={cn(
                'px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
                formData.reporting_type === 'project_only' && 'opacity-50 bg-gray-100 cursor-not-allowed'
              )}
            />
          </div>

          <div className={cn('flex flex-col gap-2')}>
            <label
              htmlFor="farm_introduction"
              className={cn('text-sm font-medium text-gray-600', formData.reporting_type === 'project_only' && 'opacity-50')}
            >
              Introduction
            </label>
            <textarea
              id="farm_introduction"
              value={formData.farm_report_introduction || ''}
              onChange={(e) => handleInputChange('farm_report_introduction', e.target.value)}
              disabled={formData.reporting_type === 'project_only'}
              rows={4}
              className={cn(
                'px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
                formData.reporting_type === 'project_only' && 'opacity-50 bg-gray-100 cursor-not-allowed'
              )}
            />
          </div>
        </div>

        <div className={cn('flex flex-col gap-4')}>
          <h3 className={cn('text-md font-semibold text-gray-700')}>Project Report</h3>

          <div className={cn('flex flex-col gap-2')}>
            <label htmlFor="project_subtitle" className={cn('text-sm font-medium text-gray-600')}>
              Subtitle
            </label>
            <input
              type="text"
              id="project_subtitle"
              value={formData.project_report_subtitle || ''}
              onChange={(e) => handleInputChange('project_report_subtitle', e.target.value)}
              className={cn('px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')}
            />
          </div>

          <div className={cn('flex flex-col gap-2')}>
            <label htmlFor="project_introduction" className={cn('text-sm font-medium text-gray-600')}>
              Introduction
            </label>
            <textarea
              id="project_introduction"
              value={formData.project_report_introduction || ''}
              onChange={(e) => handleInputChange('project_report_introduction', e.target.value)}
              rows={4}
              className={cn('px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')}
            />
          </div>
        </div>

        <div className={cn('flex flex-col gap-4')}>
          <h3 className={cn('text-md font-semibold text-gray-700')}>Disclaimer</h3>

          <div className={cn('flex flex-col gap-2')}>
            <label htmlFor="project_disclaimer" className={cn('text-sm font-medium text-gray-600')}>
              Disclaimer text appears on both project and farm-level reports
            </label>
            <textarea
              id="project_disclaimer"
              value={formData.report_disclaimer || ''}
              onChange={(e) => handleInputChange('report_disclaimer', e.target.value)}
              rows={4}
              className={cn('px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')}
            />
          </div>
        </div>

        <div className={cn('flex flex-row gap-4')}>
          <Link className="w-1/2" to={routes.projects}>
            <SecondaryButton>Cancel</SecondaryButton>
          </Link>
          <PrimaryButton className="w-1/2" type="submit" disabled={isLoading || !projectId}>
            Save
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}
